<template>
  <div data-allow-mismatch="children">
    <Skeleton
      v-if="isSkeletonVisible"
      v-bind="isBigTicketDeliveryActive ? { width: '197px' } : null"
      :height="isBigTicketDeliveryActive ? btAvailabilitySkeletonHeight : '40px'"
    />
    <div class="availability-big-ticket">
      <template v-if="!isSkeletonVisible">
        <template v-if="showNotAvailableComponent">
          <NotAvailable :current-zip-code="zipCode" />
          <component :is="UnavailableDeliveryMessage" v-if="UnavailableDeliveryMessage" class="margin-bottom-xs" />
        </template>
        <ShipToZipCode
          v-else-if="showShipToZipCodeComponent"
          v-bind="isBigTicketDeliveryActive ? { inStock: !fullBtDeliveryMessage && bigTicketAvailableForCurrentUpc } : null"
          :current-zip-code="zipCode"
          :availability-message="isBigTicketDeliveryActive ? fullBtDeliveryMessage : deliverabilityMessageForCurrentUpc"
          :big-ticket-available="bigTicketAvailableForCurrentUpc"
        />
        <NoZipCode v-else-if="showNoZipCodeComponent" />
      </template>
      <BigTicketFulfillment
        v-if="isBigTicketDeliveryActive"
        v-model:selected-option="selectedDeliveryType"
        class="wgd-delivery-methods"
        :is-loading="isDeliveryUpdatePending || isSkeletonVisible"
        :delivery-methods
        :is-unavailable="showNotAvailableComponent || !bigTicketAvailableForCurrentUpc"
        @compare-delivery-methods="openSlideout(deliveryMethods)"
      />
    </div>
  </div>
</template>
<script setup>
import { shallowRef, computed, onMounted, onScopeDispose } from 'vue';
import { useMounted } from '@vueuse/core';
import Skeleton from '@atomic-ui/skeleton/Skeleton';
import BigTicketFulfillment from '@nuxt-common/fulfillment/app/components/fulfillment/BtChips/BigTicketFulfillment';
import { pageStoreChangeListener, removeStoreChangeListener, STORE_CHANGED_ON_HEADER } from '@nuxt-common/locator/app/utils/globalStoreChange';

import { useAvailabilityBigTicket } from '../composables/useAvailabilityBigTicket';
import { useBigTicketDeliveryMethods } from '../composables/useBigTicketDeliveryMethods';
import useWhiteGloveDeliverySlideout from '~/modules/white-glove/composables/useWhiteGloveDeliverySlideout';
import useExperimentedAvailabilityComps from '~/modules/availability/composables/useExperimentedAvailabilityComps';
import { btAvailabilitySkeletonHeight } from 'config';

const {
  bigTicketAvailableForCurrentUpc,
  deliverabilityMessageForCurrentUpc,
  showShipToZipCodeComponent,
  showNotAvailableComponent,
  showNoZipCodeComponent,
  zipCode,
  setZipCode,
  dataLoadNeeded,
  isDataLoaded,
} = useAvailabilityBigTicket();

// Delay rendering until initial zip code is resolved or timed out
const waitingForInitZipCode = shallowRef(!zipCode?.value);
let zipTimeoutId = null;

const { NoZipCode, ShipToZipCode, NotAvailable, UnavailableDeliveryMessage } = useExperimentedAvailabilityComps();

const { deliveryMethods, selectedDeliveryType, isBigTicketDeliveryActive, isDeliveryUpdatePending, bigTicketCurrentDeliveryMessage } =
  useBigTicketDeliveryMethods();
const { openSlideout } = useWhiteGloveDeliverySlideout(selectedDeliveryType);

const isMounted = useMounted();

const isSkeletonVisible = computed(() => !isMounted.value || (dataLoadNeeded.value && !isDataLoaded.value) || waitingForInitZipCode.value);

const fullBtDeliveryMessage = computed(() =>
  isBigTicketDeliveryActive.value && bigTicketAvailableForCurrentUpc.value ? bigTicketCurrentDeliveryMessage.value : '',
);

// Tear down store change listener and pending fallback timer
const cleanup = () => {
  if (zipTimeoutId) {
    clearTimeout(zipTimeoutId);
    zipTimeoutId = null;
  }
  removeStoreChangeListener(STORE_CHANGED_ON_HEADER, onStoreChange);
};

const resolveZipCode = () => {
  cleanup();
  waitingForInitZipCode.value = false;
};

const onStoreChange = () => {
  setZipCode();
  resolveZipCode();
};

onMounted(() => {
  setZipCode();

  if (zipCode.value) {
    waitingForInitZipCode.value = false;
    return;
  }

  // Await header store synchronization event or fallback to manual entry after 5s
  pageStoreChangeListener(onStoreChange);

  zipTimeoutId = setTimeout(resolveZipCode, 5000);
});
onScopeDispose(cleanup);
</script>
